import type { NappletMessage } from '@napplet/core';
import {
  createCanonicalDomainResult,
  createIntentServiceUnavailableResult,
} from './domain-results.js';

import type { AclStateContainer } from './acl-state.js';
import type { SessionRegistry } from './session-registry.js';
import { handleStorageNap } from './state-handler.js';
import type { RuntimeAdapter, ServiceRegistry } from './types.js';

type DomainHandler = (windowId: string, msg: NappletMessage) => void;

type RuntimeDomainContext = {
  hooks: RuntimeAdapter;
  serviceRegistry: ServiceRegistry;
  sessionRegistry: SessionRegistry;
  aclState: AclStateContainer;
};

export type RuntimeDomainHandlers = {
  gamepad: DomainHandler;
  storage: DomainHandler;
  media: DomainHandler;
  keys: DomainHandler;
  notify: DomainHandler;
  theme: DomainHandler;
  config: DomainHandler;
  resource: DomainHandler;
  cvm: DomainHandler;
  outbox: DomainHandler;
  upload: DomainHandler;
  intent: DomainHandler;
  link: DomainHandler;
  common: DomainHandler;
  lists: DomainHandler;
  serial: DomainHandler;
  ble: DomainHandler;
  webrtc: DomainHandler;
  dm: DomainHandler;
  fs: DomainHandler;
};

export function createRuntimeDomainHandlers(context: RuntimeDomainContext): RuntimeDomainHandlers {
  return {
    gamepad: (windowId, msg) => handleServiceOnlyMessage(context, 'gamepad', windowId, msg),
    storage: (windowId, msg) => handleStorageMessage(context, windowId, msg),
    media: (windowId, msg) => handleMediaMessage(context, windowId, msg),
    keys: (windowId, msg) => handleKeysMessage(context, windowId, msg),
    notify: (windowId, msg) => handleNotifyMessage(context, windowId, msg),
    theme: (windowId, msg) => handleThemeMessage(context, windowId, msg),
    config: (windowId, msg) => handleServiceOnlyMessage(context, 'config', windowId, msg),
    resource: (windowId, msg) => handleServiceOnlyMessage(context, 'resource', windowId, msg),
    cvm: (windowId, msg) => handleServiceOnlyMessage(context, 'cvm', windowId, msg),
    outbox: (windowId, msg) => handleServiceOnlyMessage(context, 'outbox', windowId, msg),
    upload: (windowId, msg) => handleServiceOnlyMessage(context, 'upload', windowId, msg),
    intent: (windowId, msg) => handleServiceOnlyMessage(context, 'intent', windowId, msg),
    link: (windowId, msg) => handleServiceOnlyMessage(context, 'link', windowId, msg),
    common: (windowId, msg) => handleServiceOnlyMessage(context, 'common', windowId, msg),
    lists: (windowId, msg) => handleServiceOnlyMessage(context, 'lists', windowId, msg),
    serial: (windowId, msg) => handleServiceOnlyMessage(context, 'serial', windowId, msg),
    ble: (windowId, msg) => handleServiceOnlyMessage(context, 'ble', windowId, msg),
    webrtc: (windowId, msg) => handleServiceOnlyMessage(context, 'webrtc', windowId, msg),
    dm: (windowId, msg) => handleServiceOnlyMessage(context, 'dm', windowId, msg),
    fs: (windowId, msg) => handleServiceOnlyMessage(context, 'fs', windowId, msg),
  };
}

function handleStorageMessage(context: RuntimeDomainContext, windowId: string, msg: NappletMessage): void {
  const { aclState, hooks, sessionRegistry } = context;
  handleStorageNap(windowId, msg, hooks.sendToNapplet, sessionRegistry, aclState, hooks.statePersistence);
}

function handleMediaMessage(context: RuntimeDomainContext, windowId: string, msg: NappletMessage): void {
  const { hooks, serviceRegistry } = context;
  const mediaService = serviceRegistry['media'];
  if (mediaService) {
    mediaService.handleMessage(windowId, msg, (resp: NappletMessage) => hooks.sendToNapplet(windowId, resp));
    return;
  }
  if (msg.type === 'media.session.create') {
    const m = msg as NappletMessage & { id?: string; owner?: string; sessionId?: string };
    if (m.owner !== 'napplet' && m.owner !== 'shell') {
      hooks.sendToNapplet(windowId, {
        type: 'media.session.create.result',
        id: m.id ?? '',
        error: 'missing owner',
      } as NappletMessage);
      return;
    }
    if (m.owner === 'shell') {
      hooks.sendToNapplet(windowId, {
        type: 'media.session.create.result',
        id: m.id ?? '',
        owner: 'shell',
        error: 'unsupported owner mode',
      } as NappletMessage);
      return;
    }
    hooks.sendToNapplet(windowId, {
      type: 'media.session.create.result',
      id: m.id ?? '',
      owner: m.owner,
      error: 'media service unavailable',
    } as NappletMessage);
  }
}

function handleKeysMessage(context: RuntimeDomainContext, windowId: string, msg: NappletMessage): void {
  const { hooks, serviceRegistry, sessionRegistry } = context;
  if (msg.type === 'keys.forward') {
    if (!sessionRegistry.getEntryByWindowId(windowId)) return;
    forwardHotkey(hooks, msg);
    return;
  }
  const keysService = serviceRegistry['keys'];
  if (keysService) {
    keysService.handleMessage(windowId, msg, (resp: NappletMessage) => hooks.sendToNapplet(windowId, resp));
    return;
  }
  if (msg.type === 'keys.registerAction') sendRegisterActionResult(hooks, windowId, msg);
}

function forwardHotkey(hooks: RuntimeAdapter, msg: NappletMessage): void {
  const m = msg as NappletMessage & {
    key?: string; code?: string;
    ctrl?: boolean; alt?: boolean; shift?: boolean; meta?: boolean;
  };
  hooks.hotkeys.executeHotkeyFromForward({
    key: m.key ?? '',
    code: m.code ?? '',
    ctrlKey: !!m.ctrl,
    altKey: !!m.alt,
    shiftKey: !!m.shift,
    metaKey: !!m.meta,
  });
}

function sendRegisterActionResult(hooks: RuntimeAdapter, windowId: string, msg: NappletMessage): void {
  const m = msg as NappletMessage & {
    id?: string; action?: { id: string; defaultKey?: string };
  };
  hooks.sendToNapplet(windowId, {
    type: 'keys.registerAction.result',
    id: m.id ?? '',
    actionId: m.action?.id ?? '',
  } as NappletMessage);
}

function handleNotifyMessage(context: RuntimeDomainContext, windowId: string, msg: NappletMessage): void {
  const { hooks, serviceRegistry } = context;
  const notifyService = serviceRegistry['notify'];
  if (notifyService) {
    notifyService.handleMessage(windowId, msg, (resp: NappletMessage) => hooks.sendToNapplet(windowId, resp));
    return;
  }
  if (msg.type === 'notify.send') {
    const m = msg as NappletMessage & { id?: string };
    hooks.sendToNapplet(windowId, { type: 'notify.send.result', id: m.id ?? '', notificationId: `shell-${Date.now()}` } as NappletMessage);
  } else if (msg.type === 'notify.permission.request') {
    const m = msg as NappletMessage & { id?: string };
    hooks.sendToNapplet(windowId, { type: 'notify.permission.result', id: m.id ?? '', granted: true } as NappletMessage);
  }
}

function handleThemeMessage(context: RuntimeDomainContext, windowId: string, msg: NappletMessage): void {
  const { hooks, serviceRegistry } = context;
  const themeService = serviceRegistry['theme'];
  if (themeService) {
    themeService.handleMessage(windowId, msg, (resp: NappletMessage) => hooks.sendToNapplet(windowId, resp));
    return;
  }
  const fallback = createCanonicalDomainResult(msg);
  if (fallback) hooks.sendToNapplet(windowId, fallback);
}

function handleServiceOnlyMessage(
  context: RuntimeDomainContext,
  name: 'gamepad' | 'config' | 'resource' | 'cvm' | 'outbox' | 'upload' | 'intent' | 'link' | 'common' | 'lists' | 'serial' | 'ble' | 'webrtc' | 'dm' | 'fs',
  windowId: string,
  msg: NappletMessage,
): void {
  if (
    name === 'intent'
    && msg.type !== 'intent.invoke'
    && msg.type !== 'intent.available'
    && msg.type !== 'intent.handlers'
  ) {
    return;
  }
  const service = context.serviceRegistry[name];
  if (!service) {
    if (name === 'intent') {
      const result = createIntentServiceUnavailableResult(msg);
      if (result) context.hooks.sendToNapplet(windowId, result);
    }
    return;
  }
  service.handleMessage(windowId, msg, (resp: NappletMessage) => context.hooks.sendToNapplet(windowId, resp));
}
